import { datadogLogs } from '@datadog/browser-logs'
import { PfmContext } from 'containers/PageFlowManager/PageFlowManager'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { PanelSupplierCode, ResponseChoiceValue } from 'model/common'
import {
  CategoricalResponseChoiceInput,
  Matrix,
  ResponseOptionChoice,
  ResponseType,
  toCategoricalChoice
} from 'model/responseChoice'
import { useContext } from 'react'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { getUtcOffset } from 'utils/datetime'
import { usePostCallbackWithRetry } from './usePostCallback'
import { useQuestionnaireParams } from './useQuestionnaireParams'

interface AddCategoricalMatrixResponsePayload {
  survey_id: string
  supplier_respondent_id: string
  panel_supplier_code: PanelSupplierCode
  categorical_choices: CategoricalResponseChoiceInput[]
  entry_position: number
  previous_entry_position: number | null
  context_position: number
  rendered_context_position: number
  question_type_code: string
  time_zone: string
  utc_offset: string
}

const useSendCategoricalMatrix = () => {
  const post = usePostCallbackWithRetry<AddCategoricalMatrixResponsePayload>()
  const { surveyId, respondentId, panelSupplier } = useQuestionnaireParams()
  const {
    respondentProgress: [progress, setProgress]
  } = useContext<AppState>(AppStateContext)
  const { next } = useContext(PfmContext)

  const sendCategoricalMatrix = async ({
    entryPosition,
    renderedEntryPosition,
    contextPosition,
    renderedContextPosition,
    questionTypeCode,
    allMatrixRowResponses
  }: {
    entryPosition: number
    renderedEntryPosition: number
    contextPosition: number
    renderedContextPosition: number
    questionTypeCode: string
    allMatrixRowResponses: Array<{
      questionId: string
      responseChoices: ResponseOptionChoice[]
      matrix: Matrix
    }>
  }) => {
    const payload: AddCategoricalMatrixResponsePayload = {
      survey_id: surveyId,
      supplier_respondent_id: respondentId,
      panel_supplier_code: panelSupplier as PanelSupplierCode,
      categorical_choices: allMatrixRowResponses.map((rowResponse) => ({
        matrix_title_id: rowResponse.matrix.matrixTitleId,
        question_id: rowResponse.questionId,
        rendered_entry_position: renderedEntryPosition,
        row_position: rowResponse.matrix.rowPosition,
        rendered_row_position: rowResponse.matrix.renderedRowPosition,
        response_type: ResponseType.Categorical,
        response_choices: rowResponse.responseChoices.map((choice) => ({
          response_option_id: choice.responseOptionId!,
          categorical_choice: toCategoricalChoice(
            choice.value as ResponseChoiceValue
          ),
          text_entry: null, // Text entry is not applicable for categorical matrix choices
          response_option_position: choice.position ?? 0,
          rendered_response_option_position:
            choice.renderedPosition ?? choice.position ?? 0
        }))
      })),
      entry_position: entryPosition,
      previous_entry_position: progress?.previousEntryPosition ?? null,
      context_position: contextPosition,
      rendered_context_position: renderedContextPosition,
      question_type_code: questionTypeCode,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset: getUtcOffset()
    }

    const url = coreRespondentUrl(
      `/surveys/${surveyId}/responses/categorical/matrix`
    )

    const requestStartTime = performance.now()

    const promise = post(payload, url)
      .then(() => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.info('Categorical matrix response was sent', {
          payload,
          url,
          requestDurationMs: Math.round(requestDuration)
        })
      })
      .catch((err) => {
        const requestDuration = performance.now() - requestStartTime
        if (progress) {
          setProgress({
            ...progress,
            isQualityTerminated: true
          })
          next()
        } else {
          throw new Error(
            'Could not find respondent progress in useSendCategoricalMatrix'
          )
        }
        datadogLogs.logger.error(
          'Network Failure: Categorical matrix response failed to be sent',
          {
            err,
            payload,
            url,
            payloadSize: JSON.stringify(payload).length,
            requestDurationMs: Math.round(requestDuration)
          }
        )
      })

    return promise
  }

  return { sendCategoricalMatrix }
}

export default useSendCategoricalMatrix
